// PricingCard — $20/mo plan card with subscribe CTA
// Displays plan features and triggers Stripe checkout.

import { useState } from "react";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { useSubscribe } from "../../hooks/useCredits";

interface PricingCardProps {
  currentStatus: "free" | "active" | "past_due" | "cancelled";
  className?: string;
}

const PLAN_FEATURES = [
  "20 content credits per month",
  "All 6 AI agents",
  "Multi-platform publishing",
  "Brand voice learning",
  "Performance analytics",
  "Priority support",
];

export function PricingCard({ currentStatus, className }: PricingCardProps) {
  const subscribe = useSubscribe();
  const [error, setError] = useState<string | null>(null);
  const isActive = currentStatus === "active";

  async function handleSubscribe() {
    setError(null);
    try {
      const result = await subscribe.mutateAsync({
        success_url: `${window.location.origin}/usage?session=success`,
        cancel_url: `${window.location.origin}/usage?session=cancelled`,
      });

      const checkoutUrl = result.data?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        setError("Failed to get checkout URL. Please try again.");
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to start checkout";
      setError(message);
    }
  }

  return (
    <Card className={className}>
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900">Monthly credits</h3>
        <div className="mt-2 flex items-baseline justify-center gap-1">
          <span className="text-4xl font-bold text-slate-900">$20</span>
          <span className="text-sm text-slate-500">/month</span>
        </div>
        {isActive && (
          <Badge variant="success" className="mt-2">
            Current Plan
          </Badge>
        )}
      </div>

      <ul className="mt-6 space-y-3">
        {PLAN_FEATURES.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm">
            <svg
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-slate-600">{feature}</span>
          </li>
        ))}
      </ul>

      {error && (
        <p className="mt-4 text-center text-sm text-red-600">{error}</p>
      )}

      <Button
        className="mt-6 w-full"
        size="lg"
        onClick={handleSubscribe}
        loading={subscribe.isPending}
        disabled={isActive}
      >
        {isActive ? "Subscribed" : "Subscribe Now"}
      </Button>

      {currentStatus === "cancelled" && (
        <p className="mt-2 text-center text-xs text-slate-500">
          Re-subscribe to continue receiving monthly credits.
        </p>
      )}
    </Card>
  );
}
